'use client';

import { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { commissionActivityUrl } from '../../configurations/api/url';
import { api } from '../../configurations/api';
import { httpRequest } from '../../redux/types';
import { formatCommissionUsdCents } from '../../utils/commissionWallet';
import { buildCommissionShareUrl, localizeCommissionShareUrl } from '../../utils/commissionShareUrl';
import type { ICommissionActivityPayload, ICommissionActivityRow } from '../../types/global/commissionActivity.types';

const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';
const SURFACE = '#1a1919';
const SURFACE_LOW = '#131313';

export interface CommissionActivityModalProps {
    open: boolean;
    onClose: () => void;
    enrollmentId?: string;
    programId?: string;
    campaignId?: string;
}

function formatWhen(iso?: string | null) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    } catch {
        return iso;
    }
}

function ActivityRow({ row, isAppInstall, kind }: { row: ICommissionActivityRow; isAppInstall?: boolean; kind?: string }) {
    const isRefund = row.event === 'refunded' || row.status === 'voided';
    const isWebVisit = kind === 'web_visit';
    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: SURFACE_LOW,
                borderLeft: `3px solid ${isRefund ? '#f87171' : MINT}`,
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}>
                    {isAppInstall
                        ? (isWebVisit ? 'Web visit recorded' : 'App install recorded')
                        : (row.buyer?.display_name || 'Buyer')}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: isRefund ? '#f87171' : MINT,
                    }}>
                    {isRefund ? '−' : '+'}
                    {formatCommissionUsdCents(row.commission_cents)}
                </Typography>
            </Box>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: ON_VARIANT }}>
                {isRefund ? 'Refunded' : 'Earned'} · {formatWhen(row.occurred_at)}
            </Typography>
            {!isAppInstall && (
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: ON_VARIANT, mt: 0.5 }}>
                    Paid {formatCommissionUsdCents(row.base_amount_cents)}
                    {row.commission_rate != null ? ` · your cut ${row.commission_rate}%` : ''}
                </Typography>
            )}
            {isRefund && row.void_reason ? (
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 11, color: '#f87171', mt: 0.5 }}>
                    {row.void_reason.replace(/_/g, ' ')}
                </Typography>
            ) : null}
        </Box>
    );
}

export function CommissionActivityModal({
    open,
    onClose,
    enrollmentId,
    programId,
    campaignId,
}: CommissionActivityModalProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [payload, setPayload] = useState<ICommissionActivityPayload | null>(null);
    const [copied, setCopied] = useState(false);

    const load = useCallback(async () => {
        if (!enrollmentId && !programId && !campaignId) return;
        setLoading(true);
        setError(null);
        try {
            const qs = new URLSearchParams();
            if (enrollmentId) qs.set('enrollment_id', enrollmentId);
            if (programId) qs.set('program_id', programId);
            if (campaignId) qs.set('campaign_id', campaignId);
            const response = await api(`${commissionActivityUrl}?${qs.toString()}`, httpRequest.GET, null);
            const data = response?.data?.data ?? response?.data ?? null;
            setPayload(data);
        } catch (err: any) {
            setError(err?.data?.message || err?.message || 'Could not load activity');
            setPayload(null);
        } finally {
            setLoading(false);
        }
    }, [enrollmentId, programId, campaignId]);

    useEffect(() => {
        if (open) void load();
    }, [open, load]);

    const summary = payload?.summary;
    const isAppInstall = summary?.kind === 'app_install' || summary?.kind === 'web_visit';
    // Circuul links must use the API origin — skip localizeCommissionShareUrl which rewrites to window.location.origin
    const shareUrl = isAppInstall
        ? buildCommissionShareUrl({
              code: summary?.code || '',
              kind: summary?.kind,
              shareUrlFromApi: summary?.share_url,
          })
        : (localizeCommissionShareUrl(summary?.share_url) ||
              buildCommissionShareUrl({
                  code: summary?.code || '',
                  kind: summary?.kind || 'contest',
                  contestSlug: summary?.contest_slug,
                  shareUrlFromApi: summary?.share_url,
              }));

    const handleCopy = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /** ignore */
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: SURFACE,
                    backgroundImage: 'none',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.08)',
                },
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    pt: 2,
                    pb: 1,
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff' }}>
                    {loading || !summary
                        ? 'Activity'
                        : isAppInstall
                        ? (summary.kind === 'web_visit' ? 'Web visit activity' : 'Install activity')
                        : 'Commission activity'}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ color: ON_VARIANT }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <DialogContent sx={{ pt: 1, pb: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={28} sx={{ color: GOLD }} />
                    </Box>
                ) : error ? (
                    <Typography sx={{ color: '#f87171', fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14 }}>
                        {error}
                    </Typography>
                ) : !summary ? (
                    <Typography sx={{ color: ON_VARIANT, fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14 }}>
                        No activity yet for this campaign.
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        <Box>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: GOLD }}>
                                {summary.title}
                            </Typography>
                            {summary.code ? (
                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                        color: ON_VARIANT,
                                        letterSpacing: '0.06em',
                                    }}>
                                    {isAppInstall ? 'Code' : 'Promo'} {summary.code}
                                    {!isAppInstall && summary.rate != null ? ` · ${summary.rate}%` : ''}
                                </Typography>
                            ) : null}
                        </Box>

                        {shareUrl ? (
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    bgcolor: SURFACE_LOW,
                                    border: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        letterSpacing: '0.12em',
                                        textTransform: 'uppercase',
                                        color: ON_VARIANT,
                                        mb: 0.75,
                                    }}>
                                    {isAppInstall ? 'Your referral link' : 'Your promo link'}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                        color: '#fff',
                                        wordBreak: 'break-all',
                                        mb: 1,
                                    }}>
                                    {shareUrl}
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => void handleCopy()}
                                    startIcon={
                                        copied ? (
                                            <CheckIcon sx={{ fontSize: 14 }} />
                                        ) : (
                                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                                        )
                                    }
                                    sx={{
                                        color: GOLD,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                    }}>
                                    {copied ? 'Copied' : isAppInstall ? 'Copy referral link' : 'Copy link'}
                                </Button>
                            </Box>
                        ) : null}

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 1fr',
                                gap: 1,
                            }}>
                            {[
                                { label: 'Pending', value: summary.pending_cents, color: GOLD },
                                { label: 'Cleared', value: summary.cleared_cents, color: MINT },
                                { label: 'Refunded', value: summary.voided_cents, color: '#f87171' },
                            ].map(card => (
                                <Box
                                    key={card.label}
                                    sx={{
                                        p: 1.25,
                                        borderRadius: 1.5,
                                        bgcolor: SURFACE_LOW,
                                        border: '1px solid rgba(255,255,255,0.05)',
                                    }}>
                                    <Typography
                                        sx={{
                                            fontSize: 9,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            color: ON_VARIANT,
                                            mb: 0.5,
                                        }}>
                                        {card.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 15,
                                            color: card.color,
                                        }}>
                                        {formatCommissionUsdCents(card.value)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: ON_VARIANT }}>
                            {summary.earned_count} paid · {summary.refunded_count} refunded · total earned{' '}
                            {formatCommissionUsdCents(summary.total_earned_cents)}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 11,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: ON_VARIANT,
                            }}>
                            Activity log
                        </Typography>

                        {(payload?.activities?.length ?? 0) === 0 ? (
                            <Typography sx={{ color: ON_VARIANT, fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13 }}>
                                {isAppInstall
                                    ? 'No recorded installs yet. Share your referral link to start earning.'
                                    : 'No attributed payments yet. Share your link to start earning.'}
                            </Typography>
                        ) : (
                            <Stack spacing={1.25} sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                                {payload!.activities.map(row => (
                                    <ActivityRow key={row.id} row={row} isAppInstall={isAppInstall} kind={summary?.kind} />
                                ))}
                            </Stack>
                        )}
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
}
